import { useEffect, useRef, useState } from 'react';
import { ChevronDown, Download } from 'lucide-react';
import {
  downloadSeatingChart,
  triggerDownload,
  type ExportFormat,
  type SeatingChartExportParams,
  type SeatingChartOrder,
} from '../../api/export';

export interface ExportSeatingMenuProps {
  eventId: string;
  /** Filter selections to forward to the backend so the export matches the user's view. */
  filters: {
    side: string[];
    rsvpStatus: string[];
    familyGroup: string[];
  };
  /** Test id applied to the trigger button. */
  'data-testid'?: string;
}

interface MenuItem {
  format: ExportFormat;
  orderBy: SeatingChartOrder;
  label: string;
}

const ITEMS: MenuItem[] = [
  { format: 'pdf', orderBy: 'table', label: 'PDF — by table' },
  { format: 'pdf', orderBy: 'name', label: 'PDF — by guest name' },
  { format: 'csv', orderBy: 'table', label: 'CSV — by table' },
  { format: 'csv', orderBy: 'name', label: 'CSV — by guest name' },
];

/**
 * Dropdown that lets the user export the current seating chart as a PDF or
 * CSV, ordered by table or by guest name. Filter selections from the guest
 * list are forwarded so the printed copy matches what's on screen.
 */
export function ExportSeatingMenu({
  eventId,
  filters,
  'data-testid': testId = 'export-seating-menu',
}: ExportSeatingMenuProps) {
  const [open, setOpen] = useState(false);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function close(e: MouseEvent) {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  async function handleClick(item: MenuItem) {
    const key = `${item.format}-${item.orderBy}`;
    setBusyKey(key);
    try {
      const params: SeatingChartExportParams = {
        format: item.format,
        orderBy: item.orderBy,
        side: filters.side.length ? filters.side : undefined,
        rsvpStatus: filters.rsvpStatus.length ? filters.rsvpStatus : undefined,
        familyGroup: filters.familyGroup.length ? filters.familyGroup : undefined,
      };
      const blob = await downloadSeatingChart(eventId, params);
      const ext = item.format === 'csv' ? 'csv' : 'pdf';
      triggerDownload(blob, `seating-chart-${eventId}-by-${item.orderBy}.${ext}`);
      setOpen(false);
    } finally {
      setBusyKey(null);
    }
  }

  return (
    <div ref={containerRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 px-3 py-2 border border-gray-300 text-gray-700 text-sm rounded-md hover:bg-gray-50"
        data-testid={testId}
      >
        <Download size={14} />
        <span className="hidden sm:inline">Export Seating</span>
        <ChevronDown size={14} className="text-gray-500" />
      </button>
      {open && (
        <div
          className="absolute right-0 mt-1 w-56 bg-white border border-gray-200 rounded-md shadow-lg z-20"
          role="menu"
        >
          {ITEMS.map((item) => {
            const key = `${item.format}-${item.orderBy}`;
            const busy = busyKey === key;
            return (
              <button
                key={key}
                type="button"
                role="menuitem"
                disabled={busy}
                onClick={() => handleClick(item)}
                className="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                data-testid={`${testId}-item-${key}`}
              >
                {busy ? 'Downloading…' : item.label}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
